<template>
  <header
    class="header"
    :class="[{ 'header--themed': !!themeColor }, { 'header--context': withContextNavigation }, themeColor]"
  >
    <div class="header__navigation header__navigation--desktop featured">
      <div class="header__logo">
        <T3Link
          :to="homeLink"
          :aria-label="translation.goHome"
        >
          <Logo />
        </T3Link>
      </div>

      <slot name="desktop-featured" />
    </div>

    <div class="header__navigation header__navigation--desktop minor">
      <slot name="desktop-minor" />
    </div>

    <div class="header__navigation header__navigation--mobile">
      <div class="header__logo">
        <T3Link
          :to="homeLink"
          :aria-label="translation.goHome"
        >
          <Logo />
        </T3Link>
      </div>

      <slot name="mobile" />
    </div>

    <Transition name="fade">
      <template v-if="withContextNavigation">
        <aside class="header__navigation header__navigation--context">
          <slot name="context" />
        </aside>
      </template>
    </Transition>
  </header>

  <template v-if="withContextNavigation">
    <div class="header-context-placeholder" />
  </template>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'

import type { BaseHeaderProps } from '~ui/types'

const props = defineProps<BaseHeaderProps>()

const isScrollingDown = ref<boolean>(false)
let previousScrollY: number = 0

const throttleDelay = 100 /* in ms */
let lastExecution: number = 0

const handleScroll = () => {
  const now = Date.now()
  if (now - lastExecution < throttleDelay) return

  const currentScrollY = window.scrollY

  if (currentScrollY > previousScrollY && currentScrollY > 118 /* $context-navigation-h + $minor-navigation-h + $header-gap * 2 */) {
    isScrollingDown.value = true
  } else {
    isScrollingDown.value = false
  }

  previousScrollY = currentScrollY
  lastExecution = now
}

watch(isScrollingDown, (state) => {
  document.documentElement.classList.toggle('scrolling-down', state)
})

onMounted(() => {
  if (props.withContextNavigation) {
    window.addEventListener('scroll', handleScroll, { passive: true })
  }
})

onBeforeUnmount(() => {
  if (props.withContextNavigation) {
    window.removeEventListener('scroll', handleScroll)
  }
})
</script>

<style lang="scss">
$header-gap: 15px;
$featured-navigation-h: 48px;
$minor-navigation-h: 44px;
$context-navigation-h: 44px + $header-gap * 2;

$header-dekstop: $featured-navigation-h + $minor-navigation-h + $header-gap * 3;
$header-mobile: $featured-navigation-h + $header-gap * 2;

:root {
  --content-offset: #{$header-mobile};

  @include media-query(md) {
    --content-offset: #{$header-dekstop};
  }
}

$header-dekstop-context: $header-dekstop + $context-navigation-h;
$header-mobile-context: $header-mobile + $context-navigation-h;

:root.context {
  --content-offset: #{$header-mobile-context};

  @include media-query(md) {
    --content-offset: #{$header-dekstop-context};
  }
}

$header-desktop-context-scrolling: $header-dekstop-context - $minor-navigation-h - $header-gap;

:root.context.scrolling-down {
  @include media-query(md) {
    --content-offset: #{$header-desktop-context-scrolling};
  }
}

html {
  scroll-padding-top: var(--content-offset, 0px);
  scroll-behavior: smooth;
}

.header {
  --subdrop-offset: #{$featured-navigation-h + $header-gap * 2};

  display: grid;
  row-gap: $header-gap;
  position: sticky;
  top: 0;
  z-index: z-index(header);
  width: 100%;
  padding-block: $header-gap;
  transition: background-color 0.2s;

  &--context {
    grid-template-rows: $featured-navigation-h $context-navigation-h;
  }

  @include media-query(md) {
    grid-template-rows: $featured-navigation-h $minor-navigation-h;
  }

  height: $header-mobile;

  &--context {
    height: $header-mobile-context;
  }

  @include media-query(md) {
    height: $header-dekstop;
  }

  background-color: color(white);
  border-bottom: 1px solid color(grey-250);
  box-shadow:
    0px 1px 2px 0px rgba(210, 210, 210, 0.1),
    0px 4px 4px 0px rgba(210, 210, 210, 0.1),
    0px 10px 6px 0px rgba(210, 210, 210, 0.05);

  &--context {
    border-bottom: none;
    box-shadow: none;
  }

  &__navigation--context {
    background-color: color(grey-50);
    border-top: 1px solid color(grey-250);
    box-shadow:
      0px 1px 2px 0px rgba(210, 210, 210, 0.1),
      0px 4px 4px 0px rgba(210, 210, 210, 0.1),
      0px 10px 6px 0px rgba(210, 210, 210, 0.05);
  }

  &--themed {
    background-color: color(dark-blue-1000);
    border-color: color(dark-blue-950);
    box-shadow:
      0px 1px 2px 0px rgba(0, 0, 0, 0.04),
      0px 4px 4px 0px rgba(0, 0, 0, 0.04),
      0px 10px 6px 0px rgba(0, 0, 0, 0.04);
  }

  &--themed &__navigation--context {
    background-color: color(dark-blue-1000);
    border-color: color(dark-blue-950);
    box-shadow:
      0px 1px 2px 0px rgba(0, 0, 0, 0.04),
      0px 4px 4px 0px rgba(0, 0, 0, 0.04),
      0px 10px 6px 0px rgba(0, 0, 0, 0.04);
  }

  &__logo {
    position: relative;
    height: 100%;
    display: flex;
    align-items: center;

    & > a {
      color: color(grey-800);
    }

    &::before {
      position: absolute;
      display: block;
      width: 1px;
      right: calc(range-clamp(8px, 24px, 1100px, 1400px) * -1);
      inset-block: 0;
      background-color: color(grey-250);
    }

    @include media-query(md) {
      margin-right: range-clamp(16px, 48px, 1100px, 1400px);

      &::before {
        content: '';
      }
    }
  }

  &--themed &__logo {
    & > a {
      color: color(white);
    }

    &::before {
      background-color: color(grey-350);
    }
  }

  &__navigation {
    @include frame-content-container(large);

    &--desktop {
      &:where(.featured) {
        display: grid;
        grid-template-columns: auto 1fr;
        grid-template-rows: 100%;
      }

      &:where(.minor) {
        transition: opacity 0.2s, visibility 0.2s;

        html.scrolling-down & {
          opacity: 0;
          visibility: hidden;
        }
      }

      @include media-query(max md) {
        display: none;
      }
    }

    &--mobile {
      display: flex;
      align-items: center;
      justify-content: space-between;

      @include media-query(md) {
        display: none;
      }
    }

    &--context {
      display: flex;
      align-items: center;
      width: 100%;
      height: $context-navigation-h;

      transition: transform 0.2s;

      @include media-query(md) {
        html.scrolling-down & {
          transform: translateY(($context-navigation-h - $header-gap) * -1);
        }
      }
    }
  }

  &-context-placeholder {
    height: $context-navigation-h;

    @include media-query(max md) {
      display: none;
    }
  }
}
</style>
